import React, { useState, useEffect, useRef } from 'react';
import { X, Bot, FlaskConical, Key, Check, ChevronRight, ChevronLeft, AlertCircle, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface AIModelMigrationWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  { id: 1, label: 'Select New LLM',         icon: Bot },
  { id: 2, label: 'Run Accuracy Test Suite', icon: FlaskConical },
  { id: 3, label: 'Update API Keys',         icon: Key },
];

interface AIProvider {
  id: string | number;
  name: string;
  provider_type: string;
  model_name: string;
  is_active: boolean;
  is_default: boolean;
  inputCost?: number;
  outputCost?: number;
}

interface TestCategoryResult {
  name: string;
  total: number;
  passed: number;
  failed: number;
}

interface TestResults {
  total: number;
  passed: number;
  failed: number;
  categories: TestCategoryResult[];
  passRate: number;
}

const STORAGE_KEY = 'wizard_ai_migration';

interface SavedState {
  step: number;
  completed: number[];
  selectedModelId: string | number;
  testResults: TestResults | null;
  testConfirmed: boolean;
  switchDone: boolean;
}

function loadSaved(): SavedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedState) : null;
  } catch {
    return null;
  }
}

const AIModelMigrationWizard: React.FC<AIModelMigrationWizardProps> = ({ isOpen, onClose }) => {
  // Lazy initialisers — loadSaved() reads localStorage only on initial mount per state slot
  const [step, setStep] = useState<number>(() => loadSaved()?.step ?? 1);
  const [completed, setCompleted] = useState<Set<number>>(() => new Set(loadSaved()?.completed ?? []));

  // Step 1 — model list
  const [models, setModels] = useState<AIProvider[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | number>(() => loadSaved()?.selectedModelId ?? '');

  // Step 2 — test suite
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestResults | null>(() => loadSaved()?.testResults ?? null);
  const [testConfirmed, setTestConfirmed] = useState<boolean>(() => loadSaved()?.testConfirmed ?? false);
  const [testProgress, setTestProgress] = useState<{ completed: number; total: number } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cost impact gate — shown as interstitial between Step 1 and Step 2
  const [costGateAcknowledged, setCostGateAcknowledged] = useState(false);

  // Step 3 — activate
  // apiKey is never persisted (security)
  const [apiKey, setApiKey] = useState('');
  const [switching, setSwitching] = useState(false);
  const [switchDone, setSwitchDone] = useState<boolean>(() => loadSaved()?.switchDone ?? false);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (!isOpen) return;
    const state: SavedState = {
      step,
      completed: Array.from(completed),
      selectedModelId,
      testResults,
      testConfirmed,
      switchDone,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [isOpen, step, completed, selectedModelId, testResults, testConfirmed, switchDone]);

  // Load models when wizard opens
  useEffect(() => {
    if (!isOpen) return;
    setModelsLoading(true);
    api.get('/api/admin/wizards/ai-migration/models')
      .then(res => setModels(res.data.providers || []))
      .catch(err => toast.error(err?.response?.data?.error || 'Failed to load models'))
      .finally(() => setModelsLoading(false));
  }, [isOpen]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const currentModel = models.find(m => m.is_default) ?? models[0];
  const targetModel = models.find(m => m.id === selectedModelId);

  const stepValid = (s: number) => completed.has(s);

  const canAdvance = () => {
    if (step === 1) return selectedModelId !== '' && selectedModelId !== currentModel?.id && costGateAcknowledged;
    if (step === 2) return testConfirmed;
    return false;
  };

  const advance = () => {
    if (!canAdvance()) return;
    setCompleted(prev => new Set(prev).add(step));
    setStep(step + 1);
  };

  const triggerTests = async () => {
    setTesting(true);
    setTestResults(null);
    setTestProgress(null);
    try {
      await api.post('/api/admin/wizards/ai-migration/run-tests', {
        modelId: selectedModelId,
        dryRun: false,
      });

      const stopPolling = (isError = false) => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        pollRef.current = null;
        pollTimeoutRef.current = null;
        setTesting(false);
        if (isError) setTestProgress(null);
      };

      // Abort polling after 10 minutes to prevent infinite loop on hung runs
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling(true);
        toast.error('Test suite timed out after 10 minutes. Check server logs.');
      }, 10 * 60 * 1000);

      // Poll for completion
      // /api/accuracy-test-suite/status returns { success, state: { status, totalCases, completedCases, summary, results } }
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get('/api/accuracy-test-suite/status');
          const state = statusRes.data?.state ?? statusRes.data;
          const runStatus = state?.status;

          // Update live progress counter on every poll tick
          if (state?.totalCases != null) {
            setTestProgress({
              completed: state.completedCases ?? 0,
              total: state.totalCases,
            });
          }

          if (runStatus === 'completed' || runStatus === 'done') {
            stopPolling();
            // Build per-module category breakdown from results array
            const resultsArr: any[] = state.results || [];
            const moduleMap: Record<string, { passed: number; total: number }> = {};
            for (const r of resultsArr) {
              const mod = r.module || 'General';
              if (!moduleMap[mod]) moduleMap[mod] = { passed: 0, total: 0 };
              moduleMap[mod].total++;
              if (r.passed) moduleMap[mod].passed++;
            }
            const categories: TestCategoryResult[] = Object.entries(moduleMap).map(([name, v]) => ({
              name,
              total: v.total,
              passed: v.passed,
              failed: v.total - v.passed,
            }));
            const total = state.totalCases ?? resultsArr.length;
            const passed = resultsArr.filter((r: any) => r.passed).length;
            const failed = total - passed;
            const passRate = total > 0 ? Math.round((passed / total) * 1000) / 10 : 0;
            setTestResults({ total, passed, failed, categories, passRate });
            setTestProgress(null);
          } else if (runStatus === 'failed' || runStatus === 'error') {
            stopPolling(true);
            toast.error('Test run failed. Check accuracy test suite logs.');
          }
        } catch {
          // Ignore transient poll errors
        }
      }, 3000);
    } catch (err: any) {
      setTesting(false);
      toast.error(err?.response?.data?.error || 'Failed to start test run');
    }
  };

  const finishSwitch = async () => {
    if (!targetModel) return;
    setSwitching(true);
    try {
      await api.post('/api/admin/wizards/ai-migration/activate', {
        providerId: targetModel.id,
        apiKey: apiKey.trim() || undefined,
      });
      setSwitchDone(true);
      setCompleted(prev => new Set(prev).add(3));
      localStorage.removeItem(STORAGE_KEY);
      toast.success(`${targetModel.name} is now the active model`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to activate model');
    } finally {
      setSwitching(false);
    }
  };

  // Cost impact — use provider_type as proxy; real cost data comes from model metadata if available
  const currentInputCost = currentModel?.inputCost ?? 0;
  const targetInputCost = targetModel?.inputCost ?? 0;
  const costDelta = currentInputCost > 0 ? ((targetInputCost - currentInputCost) / currentInputCost) * 100 : 0;
  const CostIcon = costDelta > 5 ? TrendingUp : costDelta < -5 ? TrendingDown : Minus;
  const costColor = costDelta > 5 ? 'text-red-600' : costDelta < -5 ? 'text-green-600' : 'text-gray-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">AI Model Migration</h2>
            <p className="text-amber-100 text-xs mt-0.5">Safely migrate to a new AI model. Step 2 runs all 61 golden test cases — the model won't activate until results are reviewed and confirmed. Cost impact analysis is shown as a review gate between Steps 1 and 2.</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step progress */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = stepValid(s.id);
              return (
                <React.Fragment key={s.id}>
                  <button onClick={() => isDone ? setStep(s.id) : undefined}
                    className={`flex flex-col items-center min-w-0 flex-1 ${isDone ? 'cursor-pointer' : 'cursor-default'}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      isDone ? 'bg-amber-500 border-amber-500 text-white' :
                      isActive ? 'bg-white border-amber-500 text-amber-600' :
                      'bg-gray-100 border-gray-200 text-gray-400'
                    }`}>
                      {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 text-center leading-tight ${isActive ? 'text-amber-700' : isDone ? 'text-amber-500' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 rounded transition-all ${stepValid(s.id) ? 'bg-amber-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[280px]">

          {/* Step 1 — Select New LLM */}
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Bot className="w-4 h-4 text-amber-600" />Select New LLM</h3>
              {modelsLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-amber-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading available models…</span>
                </div>
              ) : models.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">No AI providers configured. Add providers in AI Configuration first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {models.map(model => {
                    // A model is "current" if it is the default OR it is the only model (models[0] fallback)
                    const isCurrent = model.id === currentModel?.id;
                    const isSelected = selectedModelId === model.id;
                    return (
                      <button key={model.id}
                        onClick={() => { if (!isCurrent) { setSelectedModelId(model.id); setCostGateAcknowledged(false); } }}
                        disabled={isCurrent}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                          isCurrent ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed' :
                          isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
                        }`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isSelected && !isCurrent ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900">{model.name}</p>
                            {isCurrent && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">CURRENT</span>}
                          </div>
                          <p className="text-xs text-gray-500">{model.provider_type} · {model.model_name}</p>
                        </div>
                        {isSelected && !isCurrent && <Check className="w-4 h-4 text-amber-600 shrink-0" />}
                      </button>
                    );
                  })}
                  {/* No migration target available — all providers are the current model */}
                  {models.every(m => m.id === currentModel?.id) && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-1">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <p className="text-sm text-amber-800">Only one AI provider is configured. Add another provider in AI Configuration to migrate.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Cost impact review gate — shown once a valid target model is selected */}
              {targetModel && selectedModelId !== currentModel?.id && (
                <div className={`p-3 rounded-xl border-2 transition-all ${costGateAcknowledged ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
                  <p className="text-xs font-bold text-amber-900 mb-2 uppercase tracking-wide">Cost Impact Review — Required</p>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div className="bg-white rounded-lg p-2 text-center border border-amber-100">
                      <p className="text-gray-500 text-[10px]">Current model</p>
                      <p className="font-bold text-gray-800 truncate">{currentModel?.name}</p>
                      <p className="text-gray-400 text-[10px]">{currentModel?.model_name}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center gap-1">
                      <CostIcon className={`w-5 h-5 ${costColor}`} />
                      {costDelta !== 0 && (
                        <span className={`text-[10px] font-bold ${costColor}`}>
                          {costDelta > 0 ? `+${costDelta.toFixed(0)}%` : `${costDelta.toFixed(0)}%`}
                        </span>
                      )}
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border border-amber-100">
                      <p className="text-gray-500 text-[10px]">Target model</p>
                      <p className="font-bold text-gray-800 truncate">{targetModel.name}</p>
                      <p className="text-gray-400 text-[10px]">{targetModel.model_name}</p>
                    </div>
                  </div>
                  {costGateAcknowledged ? (
                    <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium">
                      <Check className="w-3.5 h-3.5" />Cost impact reviewed and acknowledged.
                    </div>
                  ) : (
                    <button
                      onClick={() => setCostGateAcknowledged(true)}
                      className="w-full py-2 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors">
                      Acknowledge cost impact &amp; continue
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Run Accuracy Test Suite */}
          {step === 2 && targetModel && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><FlaskConical className="w-4 h-4 text-amber-600" />Run Accuracy Test Suite</h3>

              {/* Compact model reminder */}
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <Bot className="w-3.5 h-3.5 shrink-0" />
                <span>Testing <strong>{targetModel.name}</strong> ({targetModel.model_name}) against 61 golden test cases.</span>
              </div>

              {!testResults ? (
                <div className="space-y-2">
                  <button onClick={triggerTests} disabled={testing}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-60 transition-colors">
                    {testing ? <><Loader2 className="w-4 h-4 animate-spin" />Running test suite…</> : <><FlaskConical className="w-4 h-4" />Run Accuracy Test Suite</>}
                  </button>
                  {/* Live progress bar during test run */}
                  {testing && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Progress</span>
                        <span>{testProgress ? `${testProgress.completed} / ${testProgress.total} cases` : 'Starting…'}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-500"
                          style={{ width: testProgress && testProgress.total > 0 ? `${Math.round((testProgress.completed / testProgress.total) * 100)}%` : '0%' }}
                        />
                      </div>
                      {testProgress && (
                        <p className="text-xs text-center text-amber-700 font-medium">
                          {Math.round((testProgress.completed / testProgress.total) * 100)}% complete
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className={`flex items-center justify-between p-3 rounded-lg border ${testResults.passRate >= 85 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div>
                      <p className={`text-sm font-bold ${testResults.passRate >= 85 ? 'text-green-800' : 'text-red-800'}`}>
                        {testResults.passed}/{testResults.total} passed — {testResults.passRate}% pass rate
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{testResults.failed} failed cases</p>
                    </div>
                    <div className={`text-2xl font-black ${testResults.passRate >= 85 ? 'text-green-600' : 'text-red-600'}`}>
                      {testResults.passRate >= 95 ? 'A' : testResults.passRate >= 85 ? 'B' : testResults.passRate >= 75 ? 'C' : 'F'}
                    </div>
                  </div>
                  {/* Scrollable category breakdown — capped at 200px to prevent overflow */}
                  {testResults.categories.length > 0 && (
                    <div className="max-h-[200px] overflow-y-auto pr-0.5">
                      <div className="grid grid-cols-2 gap-2">
                        {testResults.categories.map(cat => (
                          <div key={cat.name} className="p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                            <p className="font-medium text-gray-700">{cat.name}</p>
                            <p className="text-gray-500">{cat.passed}/{cat.total} passed</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!testConfirmed ? (
                    <button onClick={() => setTestConfirmed(true)}
                      className="w-full py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" />Confirm results &amp; proceed to activate
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <Check className="w-4 h-4 text-green-600" />
                      <p className="text-xs text-green-800 font-medium">Results reviewed and confirmed.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Update API Keys & Activate */}
          {step === 3 && targetModel && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Key className="w-4 h-4 text-amber-600" />Update API Keys</h3>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                Switching to <strong>{targetModel.name}</strong> ({targetModel.provider_type} — {targetModel.model_name}).
                {targetModel.provider_type?.toLowerCase() === 'anthropic'
                  ? ' Your existing Anthropic API key will be used.'
                  : ` Provide a valid ${targetModel.provider_type} API key below to activate.`}
              </div>
              {targetModel.provider_type?.toLowerCase() !== 'anthropic' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{targetModel.provider_type} API Key <span className="text-red-500">*</span></label>
                  <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                    placeholder="sk-…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-400 outline-none" />
                  <p className="text-xs text-gray-400 mt-1">Key is stored encrypted. Never shared with third parties.</p>
                </div>
              )}
              {!switchDone ? (
                <button
                  onClick={finishSwitch}
                  disabled={switching || (targetModel.provider_type?.toLowerCase() !== 'anthropic' && !apiKey.trim())}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 transition-colors">
                  {switching ? <><Loader2 className="w-4 h-4 animate-spin" />Activating new model…</> : <><Bot className="w-4 h-4" />Activate {targetModel.name}</>}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Check className="w-4 h-4 text-green-600 shrink-0" />
                    <p className="text-sm text-green-800 font-medium">{targetModel.name} is now active across the platform for all users.</p>
                  </div>
                  <p className="text-xs text-gray-400 text-center">Previous model deactivated. Changes take effect immediately.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" />Back
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map(s => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all ${step === s.id ? 'bg-amber-500 w-4' : stepValid(s.id) ? 'bg-amber-300' : 'bg-gray-200'}`} />
            ))}
          </div>
          {step < 3 ? (
            <button onClick={advance} disabled={!canAdvance() || modelsLoading}
              className="flex items-center gap-1.5 px-5 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={onClose} disabled={!switchDone}
              className="flex items-center gap-1.5 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Check className="w-4 h-4" />Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIModelMigrationWizard;
